import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';

import { CSS } from '@dnd-kit/utilities';
import { Plus, X } from 'lucide-react';


export interface UploadImage {
  id?: string;
  url: string;
  position?: number;
  file?: File;
  status: 'pending' | 'uploading' | 'done' | 'error';
  attempts?: number;
  errorMsg?: string;
  progress?: number;
  index?: number;
}

interface ImageUploaderProps {
  images: UploadImage[];
  onAdd: (file: File[]) => void;
  onRemove: (image: UploadImage) => void;
  onReorder: (newOrder: UploadImage[]) => void;
  onRetry: (image: UploadImage) => void;
  maxCount?: number;
  maxSize?: number;
  acceptTypes?: string[];
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({
  images,
  onAdd,
  onRemove,
  onReorder,
  onRetry,
  maxCount = 10,
  maxSize = 5 * 1024 * 1024, // Default to 5MB
  acceptTypes = ['image/jpeg', 'image/png', 'image/webp'],
}) => {
  const onDrop = useCallback((files: File[]) => {
    onAdd(files);
  }, [onAdd]);

  const {
    getRootProps,
    getInputProps,
    fileRejections,
  } = useDropzone({
    onDrop,
    accept: acceptTypes.reduce((acc, type) => ({ ...acc, [type]: [] }), {} as Record<string, string[]>),
    maxFiles: Math.max(0, maxCount - images.length),
    maxSize,
  });

  const sensors = useSensors(useSensor(PointerSensor));
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = images.findIndex(i => i.url === active.id);
      const newIndex = images.findIndex(i => i.url === over.id);
      const newArr = arrayMove(images, oldIndex, newIndex);
      onReorder(newArr);
    }
  };
  return (
    <div>
      <label className="block mb-1 text-sm font-medium text-gray-700">Imágenes</label>
      {fileRejections.map(({ file, errors }) => (
        <p key={file.name} className="text-xs text-red-500">
          {file.name}: {errors.map(e => e.message).join(', ')}
        </p>
      ))}

      <div className="relative">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} modifiers={[restrictToHorizontalAxis]}>
          <SortableContext items={images.map(img => img.url)} strategy={horizontalListSortingStrategy}>
            <div
              className={`flex items-center space-x-2 mb-4 transition ${images.length > 2 ? 'overflow-x-auto scrollbar-hide' : ''
                }`}
            >
              {images.map(img => (
                <SortableItem key={img.url} img={img} onRemove={onRemove} onRetry={onRetry} />
              ))}

              {images.length < maxCount && (
                <div
                  {...getRootProps()}
                  className={`
                    w-24 h-48
                     flex-shrink-0 flex items-center justify-center 
                     rounded-lg border-2 border-dashed bg-white border-gray-400
                     cursor-pointer sticky right-0 ml-2 hover:bg-gray-50 transition-colors
                     ${images.length > 2 ? 'sticky' : ''}`

                  }
                >
                  <input {...getInputProps()} />
                  <Plus size={32} className="text-gray-400" />
                </div>
              )}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};

interface SortableItemProps {
  img: UploadImage;
  onRemove: (img: UploadImage) => void;
  onRetry: (img: UploadImage) => void;
}

const SortableItem: React.FC<SortableItemProps> = ({ img, onRemove, onRetry }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: img.url });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <div ref={setNodeRef} style={style} {...attributes} className="relative w-48 h-48 flex-shrink-0">
      {/* Área de drag separada de la imagen */}
      <div 
        {...listeners} 
        className="absolute inset-0 cursor-move"
        style={{ cursor: 'grab' }}
      >
        <img
          src={img.url}
          alt="thumbnail"
          className={`w-full h-full object-cover rounded-lg ${img.status !== 'done' ? 'opacity-60' : ''}`}
        />
      </div>

      {/* Barra de progreso dinámica */}
      {img.status === 'uploading' && typeof img.progress === 'number' && (
        <div className="absolute bottom-0 left-0 h-1 bg-blue-600" style={{ width: `${img.progress}%` }} />
      )}

      {img.status === 'error' && img.errorMsg && (
        <div className="absolute bottom-1 left-1 right-1 bg-red-100 text-red-700 text-xs p-1 rounded">
          {img.errorMsg}
          <button className="ml-2 underline" onClick={() => onRetry(img)}>
            Reintentar
          </button>
        </div>
      )}

      <button
        className="absolute top-1 right-1 text-gray-600 bg-white rounded-full p-1 shadow cursor-pointer hover:bg-gray-100"
        onClick={(e) => { 
          e.preventDefault();
          e.stopPropagation(); 
          onRemove(img); 
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        aria-label="Eliminar imagen"
      >
        <X size={20} />
      </button>
    </div>
  );
};
